// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * One part of a machine, opened from its row on the archive page (design doc
 * §10.6.2, US-C-06): what has been in this position, and a replacement made from here.
 *
 * **Why a position, not the event list.** The question asked at the machine is "what has
 * been on this joint" — and the 更换记录 tab answers it only by paging through every event
 * and opening each. This reads the position's own lines (newest first), ending with what it
 * was filed with, so the whole life of the slot is one list.
 *
 * **Why replacing is offered from the row as well as through 记录变更.** The common change
 * is one part, found from its row. Starting from the full dialog means choosing that row
 * again among hundreds. The row's wrench icon opens this dialog straight into the
 * replacement form (`initialMode`); clicking the row itself opens it on the history. The form is
 * that same REPLACE line with the position already chosen; it goes through the same
 * endpoint, under the same version check, and records one event — a change of several
 * parts at once is still 记录变更's job.
 *
 * The new serial is checked as it is typed, as on the filing screen: a part that is already
 * on another machine is caught before the 400, and one used before says where it was.
 *
 * **A counted part** (screws, cable ties — every non-critical line) has no serial, so its
 * form is the counted line of 记录变更 instead: replace or remove, how many of the units
 * fitted, and a substitute material if the replacement is not the BOM's part. Same
 * endpoint, same single event.
 *
 * **Swapping two slots of one line** (两个电机装反了) is the other single-part change made
 * from here: pick the other slot, and both REPLACE lines go in one event. Typed as a plain
 * replacement it would be refused — the serial is installed, on this very machine — so
 * when the serial typed belongs to a sibling slot the refusal offers the swap instead.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ApiError, newIdempotencyKey } from "@/api/client";
import { api } from "@/api/endpoints";
import type { ArchiveDetail, ArchiveItem, PositionChange } from "@/api/types";
import { occurredAtNote } from "@/archive/changeTime";
import { materialInfoOf, materialOf, summarizePart } from "@/archive/partState";
import { usePermissions } from "@/hooks/usePermissions";
import { isUsableSerial, normalizeSerial } from "@/scanner/serials";
import { useSerialStatus } from "@/scanner/useSerialStatus";
import { ChangeSummary, type ChangeType, ChangeTypeSelect, needsDescription } from "./ChangeType";
import { CopyButton } from "./CopyButton";
import { DateTimeField, timeZoneLabel } from "./DateTimeField";
import { ErrorBanner } from "./ErrorBanner";
import { isValidJiraUrl, JiraLink } from "./JiraLink";
import { Modal } from "./Modal";
import { ScrappedSerialPrompt } from "./ScrappedSerialPrompt";
import { ScrappedTag } from "./ScrappedTag";
import { SerialStatusNote } from "./SerialStatusNote";

interface Props {
  archive: ArchiveDetail;
  item: ArchiveItem;
  /** Opened from a row's icon: straight into that form. From the row itself: history only. */
  initialMode?: PositionMode | null;
  onClose: () => void;
}

export function PositionDialog({ archive, item, initialMode = null, onClose }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<PositionMode | null>(initialMode);
  const [newSerial, setNewSerial] = useState("");
  const [useSubstitute, setUseSubstitute] = useState(false);
  const [materialCode, setMaterialCode] = useState("");
  const [materialName, setMaterialName] = useState("");
  const [swapWithId, setSwapWithId] = useState("");
  // Counted parts only: what the change does to them, and to how many of the units fitted.
  const counted = item.tracking_mode === "QUANTITY";
  const [countAction, setCountAction] = useState<CountAction>("REPLACE");
  const [quantity, setQuantity] = useState(String(item.quantity));
  const [changeType, setChangeType] = useState<ChangeType | "">("");
  const [reason, setReason] = useState("");
  const [jiraUrl, setJiraUrl] = useState("");
  const jiraInvalid = !isValidJiraUrl(jiraUrl);
  // `null` while the time typed is not a real one: that blocks saving rather than being
  // quietly replaced by the time of submission.
  const [occurredAt, setOccurredAt] = useState<string | null>("");
  // Set by the first press of save: from then on the form names what is still missing,
  // rather than leaving a greyed-out button nobody can explain.
  const [attempted, setAttempted] = useState(false);
  const [savedVersion, setSavedVersion] = useState<number | null>(null);

  const historyKey = ["position-history", archive.id, item.position_id];
  const history = useQuery({
    queryKey: historyKey,
    queryFn: () => api.archives.positionHistory(archive.id, item.position_id),
  });

  const serialStatus = useSerialStatus(mode === "replace" && !counted ? [newSerial] : []);
  const elsewhere = serialStatus.installed.get(normalizeSerial(newSerial));
  const newSerialState = mode === "replace" && !counted ? serialStatus.of(newSerial) : null;
  // The serial typed is on another slot of this same line on this machine: what the
  // operator means is a swap, which is offered instead of a refusal.
  const siblings = slotSiblings(archive, item);
  const typedSibling =
    elsewhere?.archive_id === archive.id
      ? siblings.find((sibling) => sibling.position_id === positionIdOf(archive, elsewhere))
      : undefined;
  const swapWith = siblings.find((sibling) => sibling.position_id === swapWithId);

  function lines() {
    if (mode === "swap" && swapWith) {
      // Both slots in one event: the server releases both serials before claiming either
      // (§8.6 step 14), so the exchange does not collide with itself. The material goes
      // with the part, so a slot that receives a different material records it.
      return [swapLine(1, item, swapWith), swapLine(2, swapWith, item)];
    }
    if (counted) {
      const substitute = countAction === "REPLACE" && useSubstitute;
      return [
        {
          line_no: 1,
          action: countAction,
          position_id: item.position_id,
          new_position: null,
          // A counted position has no serial; even an empty one is NEW_SERIAL_NOT_ALLOWED.
          old_serial: null,
          new_serial: null,
          new_material_code: substitute ? materialCode.trim() : null,
          new_material_name: substitute ? materialName.trim() : null,
          // `missing` has already refused a count that cannot be sent.
          quantity: countOf(quantity, item.quantity) ?? item.quantity,
        },
      ];
    }
    return [
      {
        line_no: 1,
        action: "REPLACE",
        position_id: item.position_id,
        new_position: null,
        old_serial: item.serial_raw,
        new_serial: newSerial.trim(),
        // Unticked is "the same material as now", which is the ordinary swap.
        new_material_code: useSubstitute ? materialCode.trim() : null,
        new_material_name: useSubstitute ? materialName.trim() : null,
        quantity: 1,
      },
    ];
  }

  const submit = useMutation({
    mutationFn: () =>
      api.maintenance.submit(
        archive.id,
        {
          base_version: archive.current_version,
          // Blank means now — the time of submission by the server's clock, as in 记录变更.
          occurred_at: occurredAt ? new Date(occurredAt).toISOString() : null,
          change_type: changeType,
          reason: reason.trim() || null,
          jira_url: jiraUrl.trim() || null,
          lines: lines(),
        },
        newIdempotencyKey(),
      ),
    onSuccess: async (event) => {
      close();
      setSavedVersion(event.result_version);
      await refresh();
    },
    onError: async (error) => {
      // Someone else moved the machine on: reload it, so the next attempt is made against
      // the version now current rather than failing the same way again.
      if (error instanceof ApiError && error.isVersionConflict) await refresh();
    },
  });

  function open(next: PositionMode, target = "") {
    submit.reset();
    setSavedVersion(null);
    setMode(next);
    setSwapWithId(target);
  }

  function close() {
    setMode(null);
    setNewSerial("");
    setUseSubstitute(false);
    setMaterialCode("");
    setMaterialName("");
    setSwapWithId("");
    setCountAction("REPLACE");
    setQuantity(String(item.quantity));
    setChangeType("");
    setReason("");
    setJiraUrl("");
    setOccurredAt("");
    setAttempted(false);
  }

  async function refresh() {
    // Everything this change is visible through: the rows, the timeline, the event list,
    // and the history of each position it touched.
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["archive", archive.id] }),
      queryClient.invalidateQueries({ queryKey: ["archive-versions", archive.id] }),
      queryClient.invalidateQueries({ queryKey: ["maintenance", archive.id] }),
      queryClient.invalidateQueries({ queryKey: ["position-history", archive.id] }),
    ]);
  }

  // Named by the labels the operator sees, in the order they appear.
  const missing: string[] = [];
  // A counted removal fits nothing, so there is no material to name.
  const substituting = useSubstitute && !(counted && countAction === "REMOVE");
  if (mode === "replace") {
    if (counted) {
      if (countOf(quantity, item.quantity) === null) missing.push(t("position.quantity"));
    } else if (!isUsableSerial(newSerial)) missing.push(t("position.newSerial"));
    if (substituting && !materialCode.trim()) missing.push(t("maintenance.newMaterialCode"));
    if (substituting && !materialName.trim()) missing.push(t("maintenance.newMaterialName"));
  }
  if (mode === "swap" && !swapWith) missing.push(t("position.swapWith"));
  if (!changeType) missing.push(t("maintenance.changeType"));
  // Only 其他 needs a description: it has nothing else to say what was done.
  if (needsDescription(changeType) && !reason.trim())
    missing.push(t(counted ? "position.changeReason" : "position.reason"));
  if (occurredAt === null)
    missing.push(t(counted ? "position.changeOccurredAt" : "position.occurredAt"));
  const timeNote = occurredAtNote(occurredAt, archive, t);
  // A serial installed elsewhere is not missing — it has its own alert under the field.
  const blocked = (mode === "replace" && Boolean(elsewhere)) || jiraInvalid || timeNote.blocked;
  const active = archive.status === "ACTIVE";
  // A swap needs a part on both sides; an empty slot is filled by a replacement.
  const { canRecord } = usePermissions();
  const canSwap = canRecord && active && Boolean(item.serial_raw) && siblings.length > 0;
  const zone = timeZoneLabel();

  return (
    <Modal title={`${item.position_name}`} onClose={onClose} wide>
      <div className="position-head">
        {/* Everything the archive knows about the part, so nobody has to go back to the list
            or the BOM to identify it. Always listed, "—" when empty: a field that appears
            only sometimes reads as one this part does not have rather than one left empty. */}
        <dl className="position-facts">
          <dt>{t("bom.positionCode")}</dt>
          <dd className="mono">{item.position_code}</dd>
          <dt>{t("bom.partName")}</dt>
          <dd>{item.part_name || "—"}</dd>
          <dt>{t("archive.position")}</dt>
          <dd>{materialInfoOf(item)}</dd>
          <dt>{t("bom.drawingNo")}</dt>
          <dd className="mono">{item.drawing_no || "—"}</dd>
          <dt>{t(counted ? "position.currentQuantity" : "position.currentSerial")}</dt>
          <dd>
            <span className="serial">{counted ? item.quantity : (item.serial_raw ?? "—")}</span>
            {!counted && <ScrappedTag item={item} />}
            {item.serial_raw && (
              <CopyButton value={item.serial_raw} label={t("position.copySerial")} />
            )}
          </dd>
          <dt>{t("position.materialCode")}</dt>
          <dd>
            <span className="mono">{item.material_code ?? "—"}</span>
            {item.material_code && (
              <CopyButton value={item.material_code} label={t("position.copyMaterialCode")} />
            )}
            {/* As on the list: the mark goes on the material, which is what differs. */}
            {item.off_bom && <span className="badge badge-off-bom">{t("archive.offBom")}</span>}
          </dd>
          <dt>{t("archive.bomVersion")}</dt>
          <dd>
            {archive.source_bom_version
              ? [archive.source_bom_name, archive.source_bom_version].filter(Boolean).join(" ")
              : "—"}
          </dd>
          <dt>{t("bom.remark")}</dt>
          <dd>{item.remark ?? "—"}</dd>
        </dl>

        {/* On the right, clear of the facts it acts on. Replacing is the row's wrench icon;
            a swap is chosen here, with the other slot, from the history that prompts it. */}
        {mode === null && canSwap && (
          <button type="button" onClick={() => open("swap")}>
            {t("position.swap")}
          </button>
        )}
      </div>

      {savedVersion !== null && (
        <div className="banner banner-info" role="status">
          {t(counted ? "position.changeSaved" : "position.saved", { version: savedVersion })}
        </div>
      )}

      {mode !== null && (
        <form
          className="panel position-replace"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            setAttempted(true);
            if (missing.length === 0 && !blocked && !submit.isPending) submit.mutate();
          }}
        >
          {mode === "replace" ? (
            <>
              {counted ? (
                <>
                  <div className="field-row">
                    <label className="field-label required" htmlFor="position-action">
                      {t("position.action")}
                    </label>
                    <div className="field-control">
                      <select
                        id="position-action"
                        value={countAction}
                        onChange={(event) => setCountAction(event.target.value as CountAction)}
                      >
                        <option value="REPLACE">{t("maintenance.replace")}</option>
                        <option value="REMOVE">{t("maintenance.remove")}</option>
                      </select>
                    </div>
                  </div>
                  <div className="field-row">
                    <label className="field-label required" htmlFor="position-quantity">
                      {t("position.quantity")}
                    </label>
                    <div className="field-control">
                      {/* All of them unless told otherwise; the limit is what is fitted,
                          read beside the field rather than learned from a refusal. */}
                      <input
                        id="position-quantity"
                        type="number"
                        inputMode="numeric"
                        min={1}
                        max={item.quantity}
                        autoFocus
                        value={quantity}
                        onChange={(event) => setQuantity(event.target.value)}
                      />
                      <span className="hint">
                        {t("position.quantityHint", { n: item.quantity })}
                      </span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="field-row">
                  <label className="field-label required" htmlFor="position-new-serial">
                    {t("position.newSerial")}
                  </label>
                  <div className="field-control">
                    <input
                      id="position-new-serial"
                      className="serial field-serial"
                      autoComplete="off"
                      required
                      placeholder={t("position.newSerialPlaceholder")}
                      // Focus lands here: the replacement part's serial is what is in hand.
                      autoFocus
                      value={newSerial}
                      onChange={(event) => setNewSerial(event.target.value)}
                    />
                    {elsewhere && (
                      <p className="hint hint-error" role="alert">
                        {t("archive.installedAt", {
                          serial: elsewhere.serial_raw,
                          deviceNo: elsewhere.device_no ?? elsewhere.archive_no,
                          productType: elsewhere.product_type_name,
                          position: elsewhere.position_name,
                          time: new Date(elsewhere.installed_at).toLocaleString(),
                        })}{" "}
                        <Link
                          to={`/archives/${elsewhere.archive_id}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {elsewhere.archive_no}
                        </Link>
                        {typedSibling && canSwap && (
                          <button
                            type="button"
                            className="position-use-swap"
                            onClick={() => open("swap", typedSibling.position_id)}
                          >
                            {t("position.useSwap", { position: typedSibling.position_name })}
                          </button>
                        )}
                      </p>
                    )}
                    {/* Not installed: say whether it is new or has been on a machine before. */}
                    {!elsewhere && <SerialStatusNote state={newSerialState} />}
                  </div>
                </div>
              )}
              {!(counted && countAction === "REMOVE") && (
                <div className="field-row">
                  <span className="field-label">{t("position.material")}</span>
                  <div className="field-control">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={useSubstitute}
                        onChange={(event) => setUseSubstitute(event.target.checked)}
                      />
                      {t("position.useSubstitute")}
                    </label>
                    {/* Unticked, the code that stays is spelled out, so an empty box is never
                      mistaken for one already filled in. */}
                    {!useSubstitute && (
                      <span className="hint">
                        {t("position.keepMaterial", {
                          code:
                            [item.material_code, item.material_name].filter(Boolean).join(" · ") ||
                            "—",
                        })}
                      </span>
                    )}
                  </div>
                </div>
              )}
              {substituting && (
                <>
                  <div className="field-row">
                    <label className="field-label required" htmlFor="position-material-code">
                      {t("maintenance.newMaterialCode")}
                    </label>
                    <div className="field-control">
                      <input
                        id="position-material-code"
                        className="mono field-serial"
                        required
                        value={materialCode}
                        onChange={(event) => setMaterialCode(event.target.value)}
                      />
                    </div>
                  </div>
                  <div className="field-row">
                    <label className="field-label required" htmlFor="position-material-name">
                      {t("maintenance.newMaterialName")}
                    </label>
                    <div className="field-control">
                      <input
                        id="position-material-name"
                        className="field-serial"
                        required
                        value={materialName}
                        onChange={(event) => setMaterialName(event.target.value)}
                      />
                    </div>
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="field-row">
              <label className="field-label required" htmlFor="position-swap-with">
                {t("position.swapWith")}
              </label>
              <div className="field-control">
                <select
                  id="position-swap-with"
                  required
                  value={swapWithId}
                  onChange={(event) => setSwapWithId(event.target.value)}
                >
                  <option value="">—</option>
                  {siblings.map((sibling) => (
                    <option key={sibling.position_id} value={sibling.position_id}>
                      {sibling.position_name} · {sibling.serial_raw}
                      {sibling.remark ? ` · ${sibling.remark}` : ""}
                    </option>
                  ))}
                </select>
                {swapWith && (
                  <p className="hint">
                    {t("position.swapPreview", {
                      here: item.position_name,
                      there: swapWith.position_name,
                      hereSerial: item.serial_raw,
                      thereSerial: swapWith.serial_raw,
                    })}
                  </p>
                )}
              </div>
            </div>
          )}
          <div className="field-row">
            <label className="field-label required" htmlFor="position-change-type">
              {t("maintenance.changeType")}
            </label>
            <div className="field-control">
              <ChangeTypeSelect
                id="position-change-type"
                value={changeType}
                onChange={setChangeType}
              />
            </div>
          </div>
          <div className="field-row">
            <label
              className={needsDescription(changeType) ? "field-label required" : "field-label"}
              htmlFor="position-reason"
            >
              {t(counted ? "position.changeReason" : "position.reason")}
            </label>
            <div className="field-control field-wide">
              {/* Several lines: a description is often the symptom and what was done. */}
              <textarea
                id="position-reason"
                required={needsDescription(changeType)}
                rows={4}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            </div>
          </div>
          <div className="field-row">
            <label className="field-label" htmlFor="position-jira">
              {t("maintenance.jiraUrl")}
            </label>
            <div className="field-control field-wide">
              <input
                id="position-jira"
                type="url"
                inputMode="url"
                placeholder={t("maintenance.jiraUrlPlaceholder")}
                aria-invalid={jiraInvalid}
                aria-describedby="position-jira-hint"
                value={jiraUrl}
                onChange={(event) => setJiraUrl(event.target.value)}
              />
              <small className={jiraInvalid ? "hint hint-error" : "hint"} id="position-jira-hint">
                {t(jiraInvalid ? "maintenance.jiraUrlInvalid" : "maintenance.jiraUrlHint")}
              </small>
            </div>
          </div>
          <div className="field-row">
            <label className="field-label" htmlFor="position-occurred">
              {t(counted ? "position.changeOccurredAt" : "position.occurredAt")}
            </label>
            <div className="field-control">
              <DateTimeField
                id="position-occurred"
                describedBy="position-occurred-hint"
                value={occurredAt ?? ""}
                onChange={setOccurredAt}
              />
              <small className={timeNote.className} id="position-occurred-hint">
                {timeNote.text ??
                  t(counted ? "position.changeOccurredAtHint" : "position.occurredAtHint")}
                <br />
                {t("datetime.timeZone", zone)}
              </small>
            </div>
          </div>
          <ErrorBanner error={submit.error} />
          {attempted && missing.length > 0 && (
            <p className="hint hint-error form-missing" role="alert">
              {t("position.missing", { fields: missing.join(t("position.listSeparator")) })}
            </p>
          )}
          <div className="form-actions">
            <button
              type="button"
              onClick={() => {
                submit.reset();
                close();
              }}
            >
              {t("common.cancel")}
            </button>
            <button type="submit" className="button-primary" disabled={submit.isPending}>
              {t(counted ? "position.changeSave" : "position.save")}
            </button>
          </div>
        </form>
      )}

      <h3 className="panel-title">{t("position.history")}</h3>
      {history.isLoading && <p className="muted">{t("common.loading")}</p>}
      <ErrorBanner error={history.error} />
      {history.data && (
        <ol className="position-history">
          {history.data.changes.map((change) => (
            <ChangeEntry key={`${change.event_id}-${change.action}`} change={change} />
          ))}
          {history.data.filed && (
            <li>
              <div className="position-history-head">
                <span className="badge">{t("position.filed")}</span>
                <span className="muted">
                  {new Date(history.data.filed.filed_at).toLocaleString()} · v1
                </span>
              </div>
              <span className="serial">
                {history.data.filed.serial ?? `× ${history.data.filed.quantity}`}
              </span>
              {(history.data.filed.material_code || history.data.filed.material_name) && (
                <span className="part-material">
                  {[history.data.filed.material_code, history.data.filed.material_name]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              )}
            </li>
          )}
          {history.data.changes.length === 0 && !history.data.filed && (
            <li className="muted">{t("position.noHistory")}</li>
          )}
        </ol>
      )}
      <ScrappedSerialPrompt
        serials={mode === "replace" && !counted ? [newSerial] : []}
        status={serialStatus}
        onRemove={() => setNewSerial("")}
      />
    </Modal>
  );
}

export type PositionMode = "replace" | "swap";

type CountAction = "REPLACE" | "REMOVE";

/** How many units a counted change covers, or null when what was typed cannot be sent:
 *  a whole number from one up to what is fitted (more is QUANTITY_EXCEEDS_CURRENT). */
function countOf(typed: string, fitted: number): number | null {
  const count = Number(typed);
  return typed.trim() && Number.isInteger(count) && count >= 1 && count <= fitted ? count : null;
}

/**
 * The other slots of the same BOM line on this machine that hold a part: `#1`, `#2`, `#3`
 * of one critical line are the same kind of part in different places, which is what makes
 * exchanging them a meaningful change. Slots of different lines are not offered — that
 * would be fitting one kind of part where the BOM calls for another.
 */
export function slotSiblings(archive: ArchiveDetail, item: ArchiveItem): ArchiveItem[] {
  if (item.origin !== "BOM_CRITICAL" || item.slot_no === null) return [];
  const line = lineCodeOf(item.position_code);
  return archive.items.filter(
    (other) =>
      other.position_id !== item.position_id &&
      other.origin === "BOM_CRITICAL" &&
      other.slot_no !== null &&
      Boolean(other.serial_raw) &&
      lineCodeOf(other.position_code) === line,
  );
}

/** `FOLD-011#2` → `FOLD-011`: the slot code is the line's code plus `#n` (§7.4). */
function lineCodeOf(positionCode: string): string {
  const at = positionCode.lastIndexOf("#");
  return at < 0 ? positionCode : positionCode.slice(0, at);
}

function positionIdOf(archive: ArchiveDetail, hit: { position_code: string }): string | undefined {
  return archive.items.find((other) => other.position_code === hit.position_code)?.position_id;
}

/** `into` receives what `from` holds — its serial, and its material when that differs. */
function swapLine(lineNo: number, into: ArchiveItem, from: ArchiveItem) {
  const sameMaterial =
    into.material_code === from.material_code && into.material_name === from.material_name;
  return {
    line_no: lineNo,
    action: "REPLACE",
    position_id: into.position_id,
    new_position: null,
    old_serial: into.serial_raw,
    new_serial: from.serial_raw,
    new_material_code: sameMaterial ? null : from.material_code,
    new_material_name: sameMaterial ? null : from.material_name,
    quantity: 1,
  };
}

function ChangeEntry({ change }: { change: PositionChange }) {
  const { t } = useTranslation();
  const before = summarizePart(change.before);
  const after = summarizePart(change.after);
  const materialBefore = materialOf(change.before);
  const materialAfter = materialOf(change.after);
  return (
    <li>
      <div className="position-history-head">
        <span className="badge">{t(`maintenance.${change.action.toLowerCase()}`)}</span>
        <span className="muted">
          {new Date(change.occurred_at).toLocaleString()} · v{change.result_version}
          {change.submitted_by_email && ` · ${change.submitted_by_email}`}
        </span>
      </div>
      <div>
        <span className="serial">{before}</span>
        {" → "}
        <span className={after !== before ? "serial is-changed" : "serial"}>{after}</span>
      </div>
      {/* Only when it moved: a substitute part is the thing to notice (§8.6.1). */}
      {materialBefore !== materialAfter && (
        <span className="part-material is-changed">
          {materialBefore ?? "—"} → {materialAfter ?? "—"}
        </span>
      )}
      <p className="position-history-reason">
        <ChangeSummary changeType={change.change_type} reason={change.reason} />{" "}
        <JiraLink url={change.jira_url} />
      </p>
    </li>
  );
}
